import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Compass, SendHorizonal, Loader2, Undo2, Check } from "lucide-react";

// Item 55: hierarchy-aware natural-language drill. The SAME visual (type, measures,
// styling) re-renders live with AI-updated dimensions/filters; context is preserved.
export function AiDrillButton({ widget, show, Renderer }) {
  const [open, setOpen] = useState(false);
  if (!show || !widget?.dataset_id) return null;
  return (
    <>
      <button data-testid="ai-drill-btn" onClick={() => setOpen(true)} title="AI drill (natural language)"
        className="absolute top-0.5 right-8 z-10 w-6 h-6 rounded-full flex items-center justify-center text-[color:var(--brand-ocean)] opacity-40 hover:opacity-100 hover:bg-[color:rgba(43,78,255,0.12)] transition-all">
        <Compass className="w-3.5 h-3.5" />
      </button>
      {open && <AiDrillDialog open={open} onOpenChange={setOpen} widget={widget} Renderer={Renderer} />}
    </>
  );
}

function AiDrillDialog({ open, onOpenChange, widget, Renderer }) {
  const [config, setConfig] = useState(widget.config || {});
  const [stack, setStack] = useState([]);      // config history for undo
  const [steps, setSteps] = useState([]);      // [{question, explanation}]
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (text) => {
    const question = (text || q).trim();
    if (!question) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/drill-widget", {
        dataset_id: widget.dataset_id, config, question,
        title: widget.title || "", widget_type: widget.type, history: steps,
      });
      setStack((s) => [...s, config]);
      setConfig(data.config);
      setSteps((s) => [...s, { question, explanation: data.explanation }]);
      setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drill failed");
    } finally { setBusy(false); }
  };
  const undo = () => {
    if (!stack.length) return;
    setConfig(stack[stack.length - 1]);
    setStack(stack.slice(0, -1));
    setSteps(steps.slice(0, -1));
  };
  const apply = () => {
    window.dispatchEvent(new CustomEvent("eip:ai-drill-apply", { detail: { widgetId: widget.id, config } }));
    toast.success("Applied to the widget — undo is available in the toolbar");
    onOpenChange(false);
  };

  const preview = { ...widget, id: `${widget.id}__aidrill`, config };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="ai-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Compass className="w-5 h-5 text-[color:var(--brand-ocean)]" />AI drill — {widget.title || widget.type}
          </DialogTitle>
          <DialogDescription>
            Tell it where to go: “break down the West region by city”, “monthly instead of yearly”, “go back up”. The visual and measures stay the same — only grouping and filters move.
          </DialogDescription>
        </DialogHeader>
        <div className="h-[320px] rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2" data-testid="ai-drill-preview">
          <Renderer widget={preview} filters={[]} />
        </div>
        {steps.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="ai-drill-breadcrumbs">
            {steps.map((s, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)] font-medium" title={s.explanation}>
                {i + 1}. {s.question.length > 42 ? s.question.slice(0, 42) + "…" : s.question}
              </span>
            ))}
          </div>
        )}
        {steps.length > 0 && <p data-testid="ai-drill-explanation" className="text-xs text-[color:var(--ink-2)]">{steps[steps.length - 1].explanation}</p>}
        <div className="flex items-center gap-2">
          <Input data-testid="ai-drill-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && run()}
            placeholder='e.g. "drill into the West region by city"' className="bg-[color:var(--surface)]" />
          <Button data-testid="ai-drill-send" size="sm" onClick={() => run()} disabled={busy || !q.trim()} className="bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}
          </Button>
          <Button data-testid="ai-drill-undo" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={undo} disabled={!stack.length} title="Undo last drill">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button data-testid="ai-drill-apply" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={apply} disabled={!steps.length} title="Apply this view to the widget (edit mode)">
            <Check className="w-4 h-4 mr-1" />Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
